import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useUiStore } from '@liniya/client-core';

/**
 * Full-screen view of one image or video, opened from a message bubble.
 *
 * A photograph from a phone is two to seven megabytes; its thumbnail is twenty
 * to ninety kilobytes and is already in the browser's cache, because the
 * conversation just displayed it. Opening the original alone means a dark
 * rectangle for as long as the download takes — seconds on a mobile link — so
 * the thumbnail stands in, blurred, until the full picture arrives.
 */
export function MediaViewer() {
  const media = useUiStore((state) => state.mediaViewer);
  const close = useUiStore((state) => state.closeMediaViewer);
  const [fullSizeReady, setFullSizeReady] = useState(false);

  const source = media?.url ?? null;
  useEffect(() => {
    setFullSizeReady(false);
    if (!source || media?.kind !== 'image' || !media.thumbnailUrl) return;
    // Decoded off-screen first: swapping the element's `src` directly would
    // blank it while the browser decodes, which is the flicker this avoids.
    const full = new Image();
    full.src = source;
    let cancelled = false;
    const ready = () => {
      if (!cancelled) setFullSizeReady(true);
    };
    full.decode().then(ready, ready);
    return () => {
      cancelled = true;
    };
  }, [media?.kind, media?.thumbnailUrl, source]);

  useEffect(() => {
    if (!media) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, media]);

  if (!media) return null;

  return (
    <div
      className="media-viewer"
      role="dialog"
      aria-label={media.name}
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <a className="media-viewer-download" href={media.url} download={media.name}>
        <Download size={16} />
        <span>Скачать</span>
      </a>
      <button className="media-viewer-close" type="button" aria-label="Закрыть" onClick={close}>
        <X size={16} />
      </button>
      {media.kind === 'video' ? (
        <video src={media.url} controls autoPlay playsInline>
          <track kind="captions" />
        </video>
      ) : (
        <img
          src={!fullSizeReady && media.thumbnailUrl ? media.thumbnailUrl : media.url}
          alt={media.name}
          className={!fullSizeReady && media.thumbnailUrl ? 'is-provisional' : undefined}
        />
      )}
    </div>
  );
}
